<script setup>
import { ref, unref, provide, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import {
  useMapGetter,
  useFunctionGetter,
} from 'dashboard/composables/store.js';

import ChatListHeader from './ChatListHeader.vue';
import ConversationList from './ConversationList.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ConversationFilter from 'next/filter/ConversationFilter.vue';
import SaveCustomView from 'next/filter/SaveCustomView.vue';
import ChatTypeTabs from './widgets/ChatTypeTabs.vue';
import DeleteCustomViews from 'dashboard/routes/dashboard/customviews/DeleteCustomViews.vue';
import AddToDealDialog from 'dashboard/components-next/Deals/AddToDealDialog.vue';
import { useDealsStore } from 'dashboard/stores/deals';
import ConversationBulkActions from './widgets/conversation/conversationBulkActions/Index.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import ConversationResolveAttributesModal from 'dashboard/components-next/ConversationWorkflow/ConversationResolveAttributesModal.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { useBulkActions } from 'dashboard/composables/chatlist/useBulkActions';
import { useFilter } from 'shared/composables/useFilter';
import { useTrack } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import {
  useCamelCase,
  useSnakeCase,
} from 'dashboard/composables/useTransformKeys';
import { useEmitter } from 'dashboard/composables/emitter';
import { useConversationRequiredAttributes } from 'dashboard/composables/useConversationRequiredAttributes';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';

import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

import InboxesAPI from 'dashboard/api/inboxes';

import wootConstants from 'dashboard/constants/globals';
import advancedFilterOptions from './widgets/conversation/advancedFilterItems';
import filterQueryGenerator from '../helper/filterQueryGenerator.js';
import languages from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import countries from 'shared/constants/countries';
import { generateValuesForEditCustomViews } from 'dashboard/helper/customViewsHelper';
import { conversationListPageURL } from '../helper/URLHelper';
import {
  isOnMentionsView,
  isOnParticipatingView,
  isOnUnattendedView,
} from '../store/modules/conversations/helpers/actionHelpers';
import { matchesFilters } from '../store/modules/conversations/helpers/filterHelpers';
import { CONVERSATION_EVENTS } from '../helper/AnalyticsHelper/events';

const props = defineProps({
  conversationInbox: { type: [String, Number], default: 0 },
  teamId: { type: [String, Number], default: 0 },
  label: { type: String, default: '' },
  conversationType: { type: String, default: '' },
  foldersId: { type: [String, Number], default: 0 },
  showConversationList: { default: true, type: Boolean },
  isOnExpandedLayout: { default: false, type: Boolean },
});

const emit = defineEmits(['conversationLoad']);
const { uiSettings } = useUISettings();
const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const store = useStore();

const resolveAttributesModalRef = ref(null);

// Assegnazioni disabilitate (fork mono-utente): la lista è sempre "tutte" lato server.
// Le tab in cima diventano un filtro di lettura: 'all' (tutte) oppure 'unread' (non letti).
const activeAssigneeTab = ref(wootConstants.ASSIGNEE_TYPE.ALL);
// Filtro di primo livello ("a monte"): 'all', 'replied' (il contatto ha risposto almeno
// una volta) o 'not_replied'. Dentro alla categoria scelta agisce poi readFilter.
const replyFilter = ref('all');
const readFilter = ref('all');
// Filtro per fase del deal: 'all' oppure l'id (stringa) di una pipeline_stage. Lato server.
const stageFilter = ref('all');
// Ricerca stile Telegram: searchInput è ciò che digiti (istantaneo), searchQuery è il valore
// "confermato" col debounce e usato nel fetch (cerca nel contenuto messaggi, tutti i canali).
const searchInput = ref('');
const searchQuery = ref('');
let searchDebounce = null;
const dealsStore = useDealsStore();
const activeStatus = ref(wootConstants.STATUS_TYPE.OPEN);
const activeSortBy = ref(wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC);
const showAdvancedFilters = ref(false);
// chatsOnView is to store the chats that are currently visible on the screen,
// which mirrors the conversationList.
const chatsOnView = ref([]);
const foldersQuery = ref({});
const showAddFoldersModal = ref(false);
const showDeleteFoldersModal = ref(false);
const isSyncing = ref(false);
const appliedFilter = ref([]);
const advancedFilterTypes = ref(
  advancedFilterOptions.map(filter => ({
    ...filter,
    attributeName: t(`FILTER.ATTRIBUTES.${filter.attributeI18nKey}`),
  }))
);

const currentUser = useMapGetter('getCurrentUser');
const chatLists = useMapGetter('getFilteredConversations');
const mineChatsList = useMapGetter('getMineChats');
const allChatList = useMapGetter('getAllStatusChats');
const unAssignedChatsList = useMapGetter('getUnAssignedChats');
const participatingChatsList = useMapGetter('getParticipatingChats');
const chatListLoading = useMapGetter('getChatListLoadingStatus');
const activeInbox = useMapGetter('getSelectedInbox');
const conversationStats = useMapGetter('conversationStats/getStats');
const appliedFilters = useMapGetter('getAppliedConversationFiltersV2');
const folders = useMapGetter('customViews/getConversationCustomViews');
const agentList = useMapGetter('agents/getAgents');
const teamsList = useMapGetter('teams/getTeams');
const inboxesList = useMapGetter('inboxes/getInboxes');
const campaigns = useMapGetter('campaigns/getAllCampaigns');
const labels = useMapGetter('labels/getLabels');
// We can't useFunctionGetter here since it needs to be called on setup?
const getTeamFn = useMapGetter('teams/getTeam');
const getConversationById = useMapGetter('getConversationById');

const {
  selectedConversations,
  selectedInboxes,
  selectConversation,
  deSelectConversation,
  selectAllConversations,
  resetBulkActions,
  isConversationSelected,
  onAssignAgent,
  onAssignLabels,
  onRemoveLabels,
} = useBulkActions();

const {
  initializeStatusAndAssigneeFilterToModal,
  initializeInboxTeamAndLabelFilterToModal,
} = useFilter({
  filteri18nKey: 'FILTER',
  attributeModel: 'conversation_attribute',
});

const { checkMissingAttributes } = useConversationRequiredAttributes();

// computed

const hasAppliedFilters = computed(() => {
  return appliedFilters.value.length !== 0;
});

const activeFolder = computed(() => {
  if (props.foldersId) {
    const activeView = folders.value.filter(
      view => view.id === Number(props.foldersId)
    );
    const [firstValue] = activeView;
    return firstValue;
  }
  return undefined;
});

const getContact = useMapGetter('contacts/getContact');
const folderContactId = useMapGetter('customViews/getActiveFolderContactId');

const activeFolderName = computed(() => {
  return activeFolder.value?.name;
});

const hasActiveFolders = computed(() => {
  return Boolean(activeFolder.value && props.foldersId !== 0);
});

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return hasAppliedFilters.value || hasActiveFolders.value;
});

const currentUserDetails = computed(() => {
  const { id, name } = currentUser.value;
  return { id, name };
});

const showAssigneeInConversationCard = computed(() => {
  return (
    hasAppliedFiltersOrActiveFolders.value ||
    activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ALL
  );
});

const currentPageFilterKey = computed(() => {
  return hasAppliedFiltersOrActiveFolders.value
    ? 'appliedFilters'
    : activeAssigneeTab.value;
});

const inbox = useFunctionGetter('inboxes/getInbox', activeInbox);
const showEmailSyncButton = computed(
  () => inbox.value?.channel_type === 'Channel::Email'
);
const currentPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  activeAssigneeTab
);
const currentFiltersPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  currentPageFilterKey
);
const hasCurrentPageEndReached = useFunctionGetter(
  'conversationPage/getHasEndReached',
  currentPageFilterKey
);

const conversationCustomAttributes = useFunctionGetter(
  'attributes/getAttributesByModel',
  'conversation_attribute'
);

// activeAssigneeTab è fisso su 'all' (fork mono-utente): il conteggio per la
// paginazione usa il totale dal meta lato server.
const activeAssigneeTabCount = computed(
  () => conversationStats.value.allCount || 0
);

const conversationListPagination = computed(() => {
  const conversationsPerPage = 50;
  const hasChatsOnView =
    chatsOnView.value &&
    Array.isArray(chatsOnView.value) &&
    !chatsOnView.value.length;
  const isNoFiltersOrFoldersAndChatListNotEmpty =
    !hasAppliedFiltersOrActiveFolders.value && hasChatsOnView;
  const isUnderPerPage =
    chatsOnView.value.length < conversationsPerPage &&
    activeAssigneeTabCount.value < conversationsPerPage &&
    activeAssigneeTabCount.value > chatsOnView.value.length;

  if (isNoFiltersOrFoldersAndChatListNotEmpty && isUnderPerPage) {
    return 1;
  }

  return currentPage.value + 1;
});

const conversationFilters = computed(() => {
  const query = searchQuery.value.trim();
  return {
    inboxId: props.conversationInbox ? props.conversationInbox : undefined,
    assigneeType: activeAssigneeTab.value,
    // Durante la ricerca mostra TUTTI gli stati (aperte/risolte/…), non solo la tab attiva.
    status: query ? 'all' : activeStatus.value,
    sortBy: activeSortBy.value,
    page: conversationListPagination.value,
    perPage: 50,
    labels: props.label ? [props.label] : undefined,
    teamId: props.teamId || undefined,
    conversationType: props.conversationType || undefined,
    // In ricerca i filtri risposta/fase sono ignorati: la ricerca è globale.
    // Filtro "a monte" applicato lato server: 'replied' | 'not_replied' | undefined (tutti).
    replied:
      query || replyFilter.value === 'all' ? undefined : replyFilter.value,
    // Filtro lettura lato server: 'read' | 'unread' | undefined (tutti). Il set torna
    // coerente su tutte le pagine; il refinement client-side (sotto) fa comunque uscire
    // una chat dai "Non letti" nell'istante in cui la apri.
    readStatus:
      query || readFilter.value === 'all' ? undefined : readFilter.value,
    // Filtro per fase del deal (lato server): id della pipeline_stage o undefined (tutte).
    pipelineStageId:
      query || stageFilter.value === 'all'
        ? undefined
        : Number(stageFilter.value),
    // Ricerca stile Telegram: contenuto messaggi lato server (tutti i canali).
    q: query || undefined,
  };
});

// Vero mentre la ricerca è attiva: nasconde le tab filtro (la ricerca è globale).
const isSearching = computed(() => !!searchQuery.value.trim());

// Fasi della pipeline di default (dal deals store) per il filtro per fase.
const pipelineStages = computed(() => {
  const pipelines = dealsStore.pipelines || [];
  const pipeline = pipelines.find(p => p.isDefault) || pipelines[0];
  return pipeline?.stages || [];
});

// Conteggio conversazioni per fase (dal server, coerente su tutte le pagine). Le chiavi
// dell'oggetto JSON sono stringhe: l'accesso con l'id numerico funziona comunque.
const stageCounts = computed(
  () => conversationStats.value.pipelineStageCounts || {}
);

// Totale per la tab "Tutte le fasi": rispecchia la categoria risposta corrente (come il
// badge "Tutti" del filtro lettura), senza dipendere da computed definiti più in basso.
const allStagesCount = computed(() => {
  if (replyFilter.value === 'replied')
    return conversationStats.value.repliedCount || 0;
  if (replyFilter.value === 'not_replied')
    return conversationStats.value.notRepliedCount || 0;
  if (replyFilter.value === 'awaiting')
    return conversationStats.value.awaitingCount || 0;
  return conversationStats.value.allCount || 0;
});

const stageTabItems = computed(() => [
  {
    key: 'all',
    name: t('CHAT_LIST.STAGE_FILTER_TABS.all'),
    count: allStagesCount.value,
  },
  ...pipelineStages.value.map(stage => ({
    key: String(stage.id),
    name: stage.name,
    count: stageCounts.value[stage.id] || 0,
  })),
]);

const activeTeam = computed(() => {
  if (props.teamId) {
    return getTeamFn.value(props.teamId);
  }
  return {};
});

const pageTitle = computed(() => {
  if (hasAppliedFilters.value) {
    return t('CHAT_LIST.TAB_HEADING');
  }
  if (inbox.value.name) {
    return inbox.value.name;
  }
  if (activeTeam.value.name) {
    return activeTeam.value.name;
  }
  if (props.label) {
    return `#${props.label}`;
  }
  if (props.conversationType === wootConstants.CONVERSATION_TYPE.MENTION) {
    return t('CHAT_LIST.MENTION_HEADING');
  }
  if (
    props.conversationType === wootConstants.CONVERSATION_TYPE.PARTICIPATING
  ) {
    return t('CONVERSATION_PARTICIPANTS.SIDEBAR_MENU_TITLE');
  }
  if (props.conversationType === wootConstants.CONVERSATION_TYPE.UNATTENDED) {
    return t('CHAT_LIST.UNATTENDED_HEADING');
  }
  if (hasActiveFolders.value) {
    return activeFolder.value.name;
  }
  return t('CHAT_LIST.TAB_HEADING');
});

function filterByAssigneeTab(conversations) {
  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ME) {
    return conversations.filter(
      c => c.meta?.assignee?.id === currentUser.value?.id
    );
  }
  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.UNASSIGNED) {
    return conversations.filter(c => !c.meta?.assignee);
  }
  return [...conversations];
}

function sortByUnreadStatus(conversations) {
  return [...conversations].sort((a, b) => {
    const unreadCountDiff = (b.unread_count || 0) - (a.unread_count || 0);
    if (unreadCountDiff !== 0) return unreadCountDiff;

    return (b.last_activity_at || 0) - (a.last_activity_at || 0);
  });
}

// Lista "base" per i filtri correnti, prima di applicare la tab lettura.
const baseConversationList = computed(() => {
  let localConversationList = [];

  if (!hasAppliedFiltersOrActiveFolders.value) {
    const filters = conversationFilters.value;
    if (
      props.conversationType === wootConstants.CONVERSATION_TYPE.PARTICIPATING
    ) {
      localConversationList = filterByAssigneeTab(
        participatingChatsList.value(filters)
      );
    } else if (activeAssigneeTab.value === 'me') {
      localConversationList = [...mineChatsList.value(filters)];
    } else if (activeAssigneeTab.value === 'unassigned') {
      localConversationList = [...unAssignedChatsList.value(filters)];
    } else {
      localConversationList = [...allChatList.value(filters)];
    }
  } else {
    localConversationList = [...chatLists.value];
  }

  if (activeFolder.value) {
    const { payload } = activeFolder.value.query;
    localConversationList = localConversationList.filter(conversation => {
      return matchesFilters(conversation, payload);
    });
  }

  return localConversationList;
});

// Conteggi Risposto/Non risposto: calcolati dal server (filtro lato server) e forniti via
// conversationStats, così restano esatti su tutte le pagine, non solo su quelle caricate.
const repliedCount = computed(() => conversationStats.value.repliedCount || 0);
const notRepliedCount = computed(
  () => conversationStats.value.notRepliedCount || 0
);
// "Da rispondere": tocca a noi (il contatto ha scritto per ultimo). Dal server, esatto su
// tutte le pagine.
const awaitingCount = computed(
  () => conversationStats.value.awaitingCount || 0
);

// Tab di primo livello: Tutti / Risposto / Non risposto / Da rispondere. Cambiarle rifetcha
// dal server (updateReplyFilter), quindi baseConversationList è già la categoria selezionata.
const replyTabItems = computed(() => [
  {
    key: 'all',
    name: t('CHAT_LIST.REPLY_FILTER_TABS.all'),
    count: conversationStats.value.allCount || 0,
  },
  {
    key: 'replied',
    name: t('CHAT_LIST.REPLY_FILTER_TABS.replied'),
    count: repliedCount.value,
  },
  {
    key: 'not_replied',
    name: t('CHAT_LIST.REPLY_FILTER_TABS.not_replied'),
    count: notRepliedCount.value,
  },
  {
    key: 'awaiting',
    name: t('CHAT_LIST.REPLY_FILTER_TABS.awaiting'),
    count: awaitingCount.value,
  },
]);

// Totale della categoria risposta corrente: badge "Tutti" del filtro lettura.
const replyScopeTotal = computed(() => {
  if (replyFilter.value === 'replied') return repliedCount.value;
  if (replyFilter.value === 'not_replied') return notRepliedCount.value;
  if (replyFilter.value === 'awaiting') return awaitingCount.value;
  return conversationStats.value.allCount || 0;
});

// Conteggi letti/non letti GLOBALI dal server (conversationStats), coerenti su tutte le
// pagine e con il badge "Tutti", non solo sulle conversazioni caricate.
const unreadConversationsCount = computed(
  () => conversationStats.value.unreadCount || 0
);
const readConversationsCount = computed(
  () => conversationStats.value.readCount || 0
);

// Tab di secondo livello: Tutti / Letti / Non letti.
const assigneeTabItems = computed(() => [
  {
    key: 'all',
    name: t('CHAT_LIST.READ_FILTER_TABS.all'),
    count: replyScopeTotal.value,
  },
  {
    key: 'read',
    name: t('CHAT_LIST.READ_FILTER_TABS.read'),
    count: readConversationsCount.value,
  },
  {
    key: 'unread',
    name: t('CHAT_LIST.READ_FILTER_TABS.unread'),
    count: unreadConversationsCount.value,
  },
]);

const conversationList = computed(() => {
  // baseConversationList è già filtrata per categoria risposta dal server; qui applichiamo
  // solo il filtro lettura (client-side, istantaneo).
  let localConversationList = baseConversationList.value;

  if (!hasAppliedFiltersOrActiveFolders.value) {
    if (readFilter.value === 'unread') {
      localConversationList = localConversationList.filter(
        c => (c.unread_count || 0) > 0
      );
    } else if (readFilter.value === 'read') {
      localConversationList = localConversationList.filter(
        c => (c.unread_count || 0) === 0
      );
    }
  }

  if (
    !hasAppliedFiltersOrActiveFolders.value &&
    activeSortBy.value === wootConstants.SORT_BY_TYPE.UNREAD
  ) {
    localConversationList = sortByUnreadStatus(localConversationList);
  }

  return localConversationList;
});

const showEndOfListMessage = computed(() => {
  if (chatListLoading.value || !conversationList.value.length) return false;
  const target = conversationStats.value.allCount || 0;
  if (target > 0 && conversationList.value.length >= target) return true;
  return hasCurrentPageEndReached.value;
});

const allConversationsSelected = computed(() => {
  return (
    conversationList.value.length === selectedConversations.value.length &&
    conversationList.value.every(el =>
      selectedConversations.value.includes(el.id)
    )
  );
});

const uniqueInboxes = computed(() => {
  return [...new Set(selectedInboxes.value)];
});

// ---------------------- Methods -----------------------
function setFiltersFromUISettings() {
  const { conversations_filter_by: filterBy = {} } = uiSettings.value;
  const { status, order_by: orderBy } = filterBy;
  activeStatus.value = status || wootConstants.STATUS_TYPE.OPEN;
  activeSortBy.value = Object.values(wootConstants.SORT_BY_TYPE).includes(
    orderBy
  )
    ? orderBy
    : wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC;
}

function emitConversationLoaded() {
  emit('conversationLoad');
}

async function onSyncEmails() {
  if (isSyncing.value) return;
  isSyncing.value = true;
  try {
    await InboxesAPI.sync(activeInbox.value);
    useAlert(t('CHAT_LIST.EMAIL_SYNC.SUCCESS'));
  } catch (error) {
    useAlert(t('CHAT_LIST.EMAIL_SYNC.ERROR'));
  } finally {
    isSyncing.value = false;
  }
}

function fetchFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  store
    .dispatch('fetchFilteredConversations', {
      queryData: filterQueryGenerator(payload),
      page,
    })
    .then(emitConversationLoaded);

  showAdvancedFilters.value = false;
}

function fetchSavedFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  store
    .dispatch('fetchFilteredConversations', {
      queryData: payload,
      page,
    })
    .then(emitConversationLoaded);
}

function onApplyFilter(payload) {
  payload = useSnakeCase(payload);
  resetBulkActions();
  foldersQuery.value = filterQueryGenerator(payload);
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  fetchFilteredConversations(payload);
}

function closeAdvanceFiltersModal() {
  showAdvancedFilters.value = false;
  appliedFilter.value = [];
}

function onUpdateSavedFilter(payload, folderName) {
  const transformedPayload = useSnakeCase(payload);
  const payloadData = {
    ...unref(activeFolder),
    name: unref(folderName),
    query: filterQueryGenerator(transformedPayload),
  };
  store.dispatch('customViews/update', payloadData);
  closeAdvanceFiltersModal();
}

function onClickOpenAddFoldersModal() {
  showAddFoldersModal.value = true;
}

function onCloseAddFoldersModal() {
  showAddFoldersModal.value = false;
}

function onClickOpenDeleteFoldersModal() {
  showDeleteFoldersModal.value = true;
}

function onCloseDeleteFoldersModal() {
  showDeleteFoldersModal.value = false;
}

function setParamsForEditFolderModal() {
  // Here we are setting the params for edit folder modal to show the existing values.

  // For agent, team, inboxes,and campaigns we get only the id's from the query.
  // So we are mapping the id's to the actual values.

  // For labels we get the name of the label from the query.
  // If we delete the label from the label list then we will not be able to show the label name.

  // For custom attributes we get only attribute key.
  // So we are mapping it to find the input type of the attribute to show in the edit folder modal.
  return {
    agents: agentList.value,
    teams: teamsList.value,
    inboxes: inboxesList.value,
    labels: labels.value,
    campaigns: campaigns.value,
    contacts: [getContact.value(folderContactId.value)],
    languages: languages,
    countries: countries,
    priority: [
      { id: 'low', name: t('CONVERSATION.PRIORITY.OPTIONS.LOW') },
      { id: 'medium', name: t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM') },
      { id: 'high', name: t('CONVERSATION.PRIORITY.OPTIONS.HIGH') },
      { id: 'urgent', name: t('CONVERSATION.PRIORITY.OPTIONS.URGENT') },
    ],
    filterTypes: advancedFilterTypes.value,
    allCustomAttributes: conversationCustomAttributes.value,
  };
}

function initializeExistingFilterToModal() {
  const statusFilter = initializeStatusAndAssigneeFilterToModal(
    activeStatus.value,
    currentUserDetails.value,
    activeAssigneeTab.value
  );
  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  if (statusFilter) {
    appliedFilter.value = [...appliedFilter.value, useCamelCase(statusFilter)];
  }

  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  const otherFilters = initializeInboxTeamAndLabelFilterToModal(
    props.conversationInbox,
    inbox.value,
    props.teamId,
    activeTeam.value,
    props.label
  ).map(useCamelCase);

  appliedFilter.value = [...appliedFilter.value, ...otherFilters];
}

function initializeFolderToFilterModal(newActiveFolder) {
  // Here we are setting the params for edit folder modal.
  //  To show the existing values. when we click on edit folder button.

  // Here we get the query from the active folder.
  // And we are mapping the query to the actual values.
  // To show in the edit folder modal by the help of generateValuesForEditCustomViews helper.
  const query = unref(newActiveFolder)?.query?.payload;
  if (!Array.isArray(query)) return;

  const newFilters = query.map(filter => {
    const transformed = useCamelCase(filter);
    const values = Array.isArray(transformed.values)
      ? generateValuesForEditCustomViews(
          useSnakeCase(filter),
          setParamsForEditFolderModal()
        )
      : [];

    return {
      attributeKey: transformed.attributeKey,
      attributeModel: transformed.attributeModel,
      customAttributeType: transformed.customAttributeType,
      filterOperator: transformed.filterOperator,
      queryOperator: transformed.queryOperator ?? 'and',
      values,
    };
  });

  appliedFilter.value = [...appliedFilter.value, ...newFilters];
}

function initalizeAppliedFiltersToModal() {
  appliedFilter.value = [...appliedFilters.value];
}

function onToggleAdvanceFiltersModal() {
  if (showAdvancedFilters.value === true) {
    closeAdvanceFiltersModal();
    return;
  }

  if (!hasAppliedFilters.value && !hasActiveFolders.value) {
    initializeExistingFilterToModal();
  }
  if (hasActiveFolders.value) {
    initializeFolderToFilterModal(activeFolder.value);
  }
  if (hasAppliedFilters.value) {
    initalizeAppliedFiltersToModal();
  }

  showAdvancedFilters.value = true;
}

function fetchConversations() {
  store.dispatch('updateChatListFilters', conversationFilters.value);
  store.dispatch('fetchAllConversations').then(emitConversationLoaded);
}

function resetAndFetchData() {
  appliedFilter.value = [];
  resetBulkActions();
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  store.dispatch('clearConversationFilters');
  if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  }
  if (props.foldersId) {
    return;
  }
  fetchConversations();
}

function loadMoreConversations() {
  if (hasCurrentPageEndReached.value || chatListLoading.value) {
    return;
  }

  if (!hasAppliedFiltersOrActiveFolders.value) {
    fetchConversations();
  } else if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  } else if (hasAppliedFilters.value) {
    fetchFilteredConversations(appliedFilters.value);
  }
}

// Aggiorna: svuota e rifetcha la pagina 1 mantenendo i filtri correnti (tab + ricerca +
// eventuali folder/filtri avanzati). Utile se un messaggio è arrivato ma la lista non si
// è aggiornata in tempo reale.
function refreshConversations() {
  resetBulkActions();
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  if (hasActiveFolders.value) {
    fetchSavedFilteredConversations(activeFolder.value.query);
  } else if (hasAppliedFilters.value) {
    fetchFilteredConversations(appliedFilters.value);
  } else {
    fetchConversations();
  }
}

function updateReadFilter(selectedTab) {
  if (readFilter.value === selectedTab) return;
  // Filtro lettura lato server (come "Risposto"): cambia il set richiesto, quindi svuota e
  // rifetcha, così i "Non letti" comprendono tutte le pagine e non solo quelle caricate. Il
  // refinement client-side (conversationList) resta e fa uscire la chat appena la apri.
  resetBulkActions();
  emitter.emit('clearSearchInput');
  readFilter.value = selectedTab;
  resetAndFetchData();
}

function updateReplyFilter(selectedTab) {
  if (replyFilter.value === selectedTab) return;
  // Filtro di primo livello lato server: cambia il set richiesto, quindi svuota e rifetcha
  // (il filtro lettura resta client-side e si applica sul nuovo set).
  emitter.emit('clearSearchInput');
  replyFilter.value = selectedTab;
  resetAndFetchData();
}

function updateStageFilter(selectedTab) {
  if (stageFilter.value === selectedTab) return;
  // Filtro per fase deal lato server: cambia il set richiesto, svuota e rifetcha.
  emitter.emit('clearSearchInput');
  stageFilter.value = selectedTab;
  resetAndFetchData();
}

// Ricerca conversazioni (contenuto messaggi) col debounce: al termine della digitazione
// aggiorna la query e rifetcha lato server.
function onConversationSearch() {
  clearTimeout(searchDebounce);
  searchDebounce = setTimeout(() => {
    const query = searchInput.value.trim();
    if (query === searchQuery.value) return;
    searchQuery.value = query;
    resetAndFetchData();
  }, 300);
}

function clearConversationSearch() {
  clearTimeout(searchDebounce);
  searchInput.value = '';
  if (searchQuery.value) {
    searchQuery.value = '';
    resetAndFetchData();
  }
}

function onBasicFilterChange(value, type) {
  if (type === 'status') {
    activeStatus.value = value;
  } else {
    activeSortBy.value = value;
  }
  resetAndFetchData();
}

function openLastSavedItemInFolder() {
  const lastItemOfFolder = folders.value[folders.value.length - 1];
  const lastItemId = lastItemOfFolder.id;
  router.push({
    name: 'folder_conversations',
    params: { id: lastItemId },
  });
}

function openLastItemAfterDeleteInFolder() {
  if (folders.value.length > 0) {
    openLastSavedItemInFolder();
  } else {
    router.push({ name: 'home' });
    fetchConversations();
  }
}

function redirectToConversationList() {
  const {
    params: { accountId, inbox_id: inboxId, label, teamId },
    name,
  } = route;

  let conversationType = '';
  if (isOnMentionsView({ route: { name } })) {
    conversationType = wootConstants.CONVERSATION_TYPE.MENTION;
  } else if (isOnParticipatingView({ route: { name } })) {
    conversationType = wootConstants.CONVERSATION_TYPE.PARTICIPATING;
  } else if (isOnUnattendedView({ route: { name } })) {
    conversationType = wootConstants.CONVERSATION_TYPE.UNATTENDED;
  }
  router.push(
    conversationListPageURL({
      accountId,
      conversationType: conversationType,
      customViewId: props.foldersId,
      inboxId,
      label,
      teamId,
    })
  );
}

async function assignPriority(priority, conversationId = null) {
  store.dispatch('setCurrentChatPriority', {
    priority,
    conversationId,
  });
  store.dispatch('assignPriority', { conversationId, priority }).then(() => {
    useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
      newValue: priority,
      from: 'Context menu',
    });
    useAlert(
      t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
        priority,
        conversationId,
      })
    );
  });
}

async function markAsUnread(conversationId) {
  try {
    await store.dispatch('markMessagesUnread', {
      id: conversationId,
    });
    redirectToConversationList();
  } catch (error) {
    // Ignore error
  }
}
async function markAsRead(conversationId) {
  try {
    await store.dispatch('markMessagesRead', {
      id: conversationId,
    });
  } catch (error) {
    // Ignore error
  }
}

async function onAssignTeam(team, conversationId = null) {
  try {
    await store.dispatch('assignTeam', {
      conversationId,
      teamId: team.id,
    });
    useAlert(
      t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.SUCCESFUL', {
        team: team.name,
        conversationId,
      })
    );
  } catch (error) {
    useAlert(t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.FAILED'));
  }
}

function toggleConversationStatus(
  conversationId,
  status,
  snoozedUntil,
  customAttributes = null
) {
  const payload = {
    conversationId,
    status,
    snoozedUntil,
  };

  if (customAttributes) {
    payload.customAttributes = customAttributes;
  }

  store.dispatch('toggleStatus', payload).then(() => {
    useAlert(t('CONVERSATION.CHANGE_STATUS'));
  });
}

function handleResolveConversation(conversationId, status, snoozedUntil) {
  if (status !== wootConstants.STATUS_TYPE.RESOLVED) {
    toggleConversationStatus(conversationId, status, snoozedUntil);
    return;
  }

  // Check for required attributes before resolving
  const conversation = getConversationById.value(conversationId);
  const currentCustomAttributes = conversation?.custom_attributes || {};
  const { hasMissing, missing } = checkMissingAttributes(
    currentCustomAttributes
  );

  if (hasMissing) {
    // Pass conversation context through the modal's API
    const conversationContext = {
      id: conversationId,
      snoozedUntil,
    };
    resolveAttributesModalRef.value?.open(
      missing,
      currentCustomAttributes,
      conversationContext
    );
  } else {
    toggleConversationStatus(conversationId, status, snoozedUntil);
  }
}

function handleResolveWithAttributes({ attributes, context }) {
  if (context) {
    const existingConversation = getConversationById.value(context.id);
    const currentCustomAttributes =
      existingConversation?.custom_attributes || {};
    const mergedAttributes = { ...currentCustomAttributes, ...attributes };

    toggleConversationStatus(
      context.id,
      wootConstants.STATUS_TYPE.RESOLVED,
      context.snoozedUntil,
      mergedAttributes
    );
  }
}

function allSelectedConversationsStatus(status) {
  if (!selectedConversations.value.length) return false;
  return selectedConversations.value.every(item => {
    return getConversationById.value(item)?.status === status;
  });
}

function toggleSelectAll(check) {
  selectAllConversations(check, conversationList);
}

useEmitter('fetch_conversation_stats', () => {
  if (hasAppliedFiltersOrActiveFolders.value) return;
  store.dispatch('conversationStats/get', conversationFilters.value);
});

// Applica un filtro conversazioni ricevuto dall'esterno (es. multi-selezione
// canali nella sidebar). Riusa il flusso "onApplyFilter" del modale filtri, in
// modo che la lista mostri le conversazioni delle inbox selezionate insieme.
useEmitter(BUS_EVENTS.APPLY_CONVERSATION_FILTER, payload => {
  if (!Array.isArray(payload) || !payload.length) return;
  appliedFilter.value = [...payload];
  store.dispatch(
    'setConversationFilters',
    useSnakeCase(JSON.parse(JSON.stringify(payload)))
  );
  onApplyFilter(payload);
});

onMounted(() => {
  // Carica le pipeline (con le fasi) per popolare il filtro per fase; silenzioso se assenti.
  dealsStore.fetchPipelines?.().catch(() => {});
  store.dispatch('setChatListFilters', conversationFilters.value);
  setFiltersFromUISettings();
  store.dispatch('setChatStatusFilter', activeStatus.value);
  store.dispatch('setChatSortFilter', activeSortBy.value);
  resetAndFetchData();
  if (hasActiveFolders.value) {
    store.dispatch('campaigns/get');
  }
});

const deleteConversationDialogRef = ref(null);
const selectedConversationId = ref(null);

async function deleteConversation() {
  try {
    await store.dispatch('deleteConversation', selectedConversationId.value);
    redirectToConversationList();
    selectedConversationId.value = null;
    deleteConversationDialogRef.value.close();
    useAlert(t('CONVERSATION.SUCCESS_DELETE_CONVERSATION'));
  } catch (error) {
    useAlert(t('CONVERSATION.FAIL_DELETE_CONVERSATION'));
  }
}

const handleDelete = conversationId => {
  selectedConversationId.value = conversationId;
  deleteConversationDialogRef.value.open();
};

const selectedChat = useMapGetter('getSelectedChat');

// "Elimina e blocca": blocca il contatto (niente più messaggi da quella chat, anche su
// Telegram tramite l'hook sul model Contact) e poi elimina la conversazione.
async function deleteAndBlock() {
  const conversation = getConversationById.value(selectedConversationId.value);
  const contactId = conversation?.meta?.sender?.id;
  try {
    if (contactId) {
      await store.dispatch('contacts/update', { id: contactId, blocked: true });
    }
    await store.dispatch('deleteConversation', selectedConversationId.value);
    redirectToConversationList();
    selectedConversationId.value = null;
    deleteConversationDialogRef.value.close();
    useAlert(t('CONVERSATION.DELETE_AND_BLOCK.SUCCESS'));
  } catch (error) {
    useAlert(t('CONVERSATION.DELETE_AND_BLOCK.ERROR'));
  }
}

// Backspace sulla conversazione aperta apre il dialog Elimina / Elimina e blocca.
// useKeyboardEvents ignora l'evento mentre stai scrivendo (input, textarea, editor).
function onDeleteShortcut(e) {
  const conversationId = selectedChat.value?.id;
  if (!conversationId) return;
  e.preventDefault();
  selectedConversationId.value = conversationId;
  deleteConversationDialogRef.value?.open();
}

useKeyboardEvents({ Backspace: { action: onDeleteShortcut } });

provide('selectConversation', selectConversation);
provide('deSelectConversation', deSelectConversation);
provide('assignAgent', onAssignAgent);
provide('assignTeam', onAssignTeam);
provide('assignLabels', onAssignLabels);
provide('removeLabels', onRemoveLabels);
provide('updateConversationStatus', handleResolveConversation);
provide('markAsUnread', markAsUnread);
provide('markAsRead', markAsRead);
provide('assignPriority', assignPriority);
provide('isConversationSelected', isConversationSelected);
provide('deleteConversation', handleDelete);

// Aprire il dialog "Aggiungi a deal" dal menu contestuale di una card (via inject).
const addToDealDialogRef = ref(null);
function openAddToDeal(conversationId, contactId) {
  addToDealDialogRef.value?.open(conversationId, contactId);
}
provide('addConversationToDeal', openAddToDeal);

watch(activeTeam, () => resetAndFetchData());

watch(
  computed(() => props.conversationInbox),
  () => resetAndFetchData()
);
watch(
  computed(() => props.label),
  () => resetAndFetchData()
);
watch(
  computed(() => props.conversationType),
  () => resetAndFetchData()
);

watch(activeFolder, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('customViews/setActiveConversationFolder', newVal || null);
  }
  resetAndFetchData();
});

watch(chatLists, () => {
  chatsOnView.value = conversationList.value;
});

watch(conversationFilters, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('updateChatListFilters', newVal);
  }
});
</script>

<template>
  <div
    class="flex flex-col flex-shrink-0 conversations-list-wrap bg-n-surface-1 relative"
    :class="[
      { hidden: !showConversationList },
      isOnExpandedLayout ? 'basis-full' : 'w-[340px] 2xl:w-[412px]',
    ]"
  >
    <slot />
    <ChatListHeader
      :page-title="pageTitle"
      :has-applied-filters="hasAppliedFilters"
      :has-active-folders="hasActiveFolders"
      :active-status="activeStatus"
      :is-on-expanded-layout="isOnExpandedLayout"
      :conversation-stats="conversationStats"
      :is-list-loading="chatListLoading && !conversationList.length"
      :show-email-sync="showEmailSyncButton"
      :is-syncing="isSyncing"
      @add-folders="onClickOpenAddFoldersModal"
      @delete-folders="onClickOpenDeleteFoldersModal"
      @filters-modal="onToggleAdvanceFiltersModal"
      @reset-filters="resetAndFetchData"
      @basic-filter-change="onBasicFilterChange"
      @sync-emails="onSyncEmails"
      @refresh="refreshConversations"
    />

    <TeleportWithDirection
      v-if="showAddFoldersModal"
      to="#saveFilterTeleportTarget"
    >
      <SaveCustomView
        v-model="appliedFilter"
        :custom-views-query="foldersQuery"
        :open-last-saved-item="openLastSavedItemInFolder"
        @close="onCloseAddFoldersModal"
      />
    </TeleportWithDirection>

    <DeleteCustomViews
      v-if="showDeleteFoldersModal"
      v-model:show="showDeleteFoldersModal"
      :active-custom-view="activeFolder"
      :custom-views-id="foldersId"
      :open-last-item-after-delete="openLastItemAfterDeleteInFolder"
      @close="onCloseDeleteFoldersModal"
    />

    <!-- Ricerca stile Telegram: barra fissa in cima alla lista, cerca nel contenuto dei
         messaggi (tutti i canali) lato server. Durante la ricerca le tab si nascondono. -->
    <div v-if="!hasAppliedFiltersOrActiveFolders" class="px-3 pt-1.5 pb-0.5">
      <div class="relative flex items-center">
        <Icon
          icon="i-lucide-search"
          class="absolute size-4 text-n-slate-10 start-2.5 pointer-events-none"
        />
        <input
          v-model="searchInput"
          type="search"
          :placeholder="$t('CHAT_LIST.SEARCH_CONVERSATIONS')"
          class="reset-base w-full h-9 text-sm rounded-lg bg-n-alpha-2 text-n-slate-12 ps-9 pe-8 outline outline-1 outline-n-weak focus:outline-n-brand"
          @input="onConversationSearch"
        />
        <button
          v-if="searchInput"
          type="button"
          class="absolute grid size-5 end-2 place-content-center rounded text-n-slate-10 hover:text-n-slate-12"
          @click="clearConversationSearch"
        >
          <Icon icon="i-lucide-x" class="size-4" />
        </button>
      </div>
    </div>

    <ChatTypeTabs
      v-if="!hasAppliedFiltersOrActiveFolders && !isSearching"
      :items="replyTabItems"
      :active-tab="replyFilter"
      is-compact
      @chat-tab-change="updateReplyFilter"
    />

    <ChatTypeTabs
      v-if="!hasAppliedFiltersOrActiveFolders && !isSearching"
      :items="assigneeTabItems"
      :active-tab="readFilter"
      is-compact
      @chat-tab-change="updateReadFilter"
    />

    <ChatTypeTabs
      v-if="
        !hasAppliedFiltersOrActiveFolders &&
        !isSearching &&
        stageTabItems.length > 1
      "
      :items="stageTabItems"
      :active-tab="stageFilter"
      is-compact
      @chat-tab-change="updateStageFilter"
    />

    <p
      v-if="!chatListLoading && !conversationList.length"
      class="flex overflow-auto justify-center items-center p-4"
    >
      {{ $t('CHAT_LIST.LIST.404') }}
    </p>
    <ConversationBulkActions
      :conversations="selectedConversations"
      :all-conversations-selected="allConversationsSelected"
      :selected-inboxes="uniqueInboxes"
      :show-open-action="allSelectedConversationsStatus('open')"
      :show-resolved-action="allSelectedConversationsStatus('resolved')"
      :show-snoozed-action="allSelectedConversationsStatus('snoozed')"
      :class="isOnExpandedLayout && 'sm:!w-[24rem] !w-full'"
      @select-all-conversations="toggleSelectAll"
    />
    <ConversationList
      :conversation-list="conversationList"
      :is-loading="chatListLoading"
      :show-end-of-list-message="showEndOfListMessage"
      :label="label"
      :team-id="teamId"
      :folders-id="foldersId"
      :conversation-type="conversationType"
      :show-assignee="showAssigneeInConversationCard"
      :is-on-expanded-layout="isOnExpandedLayout"
      @load-more="loadMoreConversations"
    />
    <Dialog
      ref="deleteConversationDialogRef"
      type="alert"
      :title="
        $t('CONVERSATION.DELETE_CONVERSATION.TITLE', {
          conversationId: selectedConversationId,
        })
      "
      :description="$t('CONVERSATION.DELETE_CONVERSATION.DESCRIPTION')"
      :show-confirm-button="false"
      :show-cancel-button="false"
      @close="selectedConversationId = null"
    >
      <template #footer>
        <div class="flex flex-col w-full gap-2">
          <Button
            type="button"
            color="ruby"
            class="w-full"
            :label="$t('CONVERSATION.DELETE_AND_BLOCK.CONFIRM')"
            @click="deleteAndBlock"
          />
          <Button
            type="button"
            color="ruby"
            variant="faded"
            class="w-full"
            :label="$t('CONVERSATION.DELETE_CONVERSATION.CONFIRM')"
            @click="deleteConversation"
          />
          <Button
            type="button"
            color="slate"
            variant="faded"
            class="w-full"
            :label="$t('DIALOG.BUTTONS.CANCEL')"
            @click="deleteConversationDialogRef.close()"
          />
        </div>
      </template>
    </Dialog>
    <AddToDealDialog ref="addToDealDialogRef" />
    <TeleportWithDirection
      v-if="showAdvancedFilters"
      to="#conversationFilterTeleportTarget"
    >
      <ConversationFilter
        v-model="appliedFilter"
        :folder-name="activeFolderName"
        :is-folder-view="hasActiveFolders"
        @apply-filter="onApplyFilter"
        @update-folder="onUpdateSavedFilter"
        @close="closeAdvanceFiltersModal"
      />
    </TeleportWithDirection>
    <ConversationResolveAttributesModal
      ref="resolveAttributesModalRef"
      @submit="handleResolveWithAttributes"
    />
  </div>
</template>
